import { MenuItem, Select, Checkbox, ListItemText } from "@mui/material";
import { MenuProps, useStyles } from "./MainSelect.styles";
import { IMainSelect } from "./MainSelectInterfaces";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MainTag from "components/MainTag";
import { useMemo } from "react";
import { allElements, notSelected } from "constants/constants";
import { useTranslation } from "react-i18next";

export const multipleDefaultValueString = "$$multiple_value_default$$";

const MainSelect = ({
    label,
    onChange,
    value,
    options,
    small,
    disabled,
    multiple,
    className,
    tagClassName,
    size,
    fullWidth = true,
    placeholder,
    labelId,
    name,
    disableMenuPropsStyles,
    renderValue,
    order,
}: IMainSelect) => {
    const classes = useStyles();
    const { t } = useTranslation();

    const sortedOptions = useMemo(() => {
        if (!order) return options;

        const priority = (id: string | number) => {
            if (id === allElements) return 0;
            if (id === notSelected) return 1;
            return 2;
        };

        const copy = [...options];
        copy.sort((a, b) => {
            const pa = priority(a.id);
            const pb = priority(b.id);
            if (pa !== pb) return pa - pb;
            const cmp = a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
            return order === "desc" ? -cmp : cmp;
        });
        return copy;
    }, [options, order]);

    return (
        <div className={classes.selectWrapper} style={{ width: fullWidth ? "100%" : "initial" }}>
            {!!label && <div className={small ? classes.smallLabel : classes.label}>{label}</div>}
            {!multiple && (
                <Select
                    name={name}
                    labelId={labelId}
                    displayEmpty={!!placeholder}
                    disabled={disabled}
                    size={size}
                    IconComponent={KeyboardArrowDownIcon}
                    className={`${classes.select} ${(small && classes.smallSelect) || ""} ${className || ""}`}
                    value={value}
                    onChange={onChange}
                    renderValue={(selectedValue) => {
                        const selectedOption = options?.find((option) => option.id === selectedValue);

                        if (selectedOption) {
                            if (renderValue) {
                                return renderValue(selectedValue);
                            }
                            return selectedOption.name;
                        } else {
                            return <span>{t("data.select_valid_option")}</span>;
                        }
                    }}
                >
                    {!!placeholder && (
                        <MenuItem disabled value="" className={classes.menuItem}>
                            {placeholder}
                        </MenuItem>
                    )}
                    {sortedOptions?.map((option) => (
                        <MenuItem key={option.id} value={option.id} className={classes.menuItem}>
                            <div className={classes.menuItemLeftContent}>
                                {!!option.icon && option.icon}
                                {option.name}{" "}
                            </div>

                            {option.tag && (
                                <MainTag small className={tagClassName}>
                                    {option.tag}
                                </MainTag>
                            )}
                        </MenuItem>
                    ))}
                </Select>
            )}
            {multiple && (
                <Select
                    name={name}
                    labelId={labelId}
                    displayEmpty={!!placeholder}
                    size={size}
                    multiple
                    disabled={disabled}
                    MenuProps={disableMenuPropsStyles ? {} : MenuProps}
                    IconComponent={KeyboardArrowDownIcon}
                    className={`${classes.select} ${(small && classes.smallSelect) || ""} ${className || ""}`}
                    style={{ width: fullWidth ? "100%" : "initial" }}
                    renderValue={(selected) => {
                        if (!Array.isArray(selected)) {
                            return selected;
                        }

                        const validSelections = selected.filter(
                            (select) =>
                                select === multipleDefaultValueString || options.some((option) => option.id === select),
                        );

                        const selectedNames = validSelections
                            .filter((select) => select !== multipleDefaultValueString)
                            .map((select) => options.find((option) => option.id === select)?.name);

                        if (validSelections.includes(multipleDefaultValueString) || selectedNames.length === 0) {
                            if (
                                placeholder &&
                                (validSelections.length === 0 || validSelections[0] === multipleDefaultValueString)
                            ) {
                                return placeholder;
                            }
                            return <span>{t("data.select_valid_option")}</span>;
                        }

                        return selectedNames.join(", ");
                    }}
                    value={value && value.length === 0 ? [multipleDefaultValueString] : value}
                    onChange={onChange}
                >
                    {!!placeholder && (
                        <MenuItem disabled value={multipleDefaultValueString} className={classes.menuItem}>
                            {placeholder}
                        </MenuItem>
                    )}
                    {sortedOptions?.map((option) => (
                        <MenuItem key={option.id} value={option.id} className={classes.menuItem}>
                            <Checkbox
                                color="info"
                                checked={Array.isArray(value) ? value.some((element) => element === option.id) : false}
                            />{" "}
                            <ListItemText>
                                {option.name} {option.tag && <MainTag small>{option.tag}</MainTag>}
                            </ListItemText>
                        </MenuItem>
                    ))}
                </Select>
            )}
        </div>
    );
};

export default MainSelect;
